import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { Blueprint, BlueprintBlockType } from 'dashboard/models/blueprint';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Report } from 'dashboard/models/report';
import {
  DashboardPageLink,
  GuideStepTipBanner,
  GuideStepWarningBanner,
  UnorderedList,
} from 'dashboard/views/DashboardSandboxHomeView/constants';
import Button from 'design-system/components/Button';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import useProductFeature from 'lib/hooks/useProductFeature';

interface ReviewReportResultsStepProps {
  reportsForSelectedBlueprint: Report[];
  isLoadingReportsForSelectedBlueprint: boolean;
  selectedBlueprint: Blueprint;
  isStepCompleted: boolean;
  isSavingCompletedStep: boolean;
  handleLoadReportsForSelectedBlueprint: (reportTemplateIds: string[]) => Promise<void>;
  saveCompletedStep: () => void;
}

const ReviewReportResultsStep = ({
  reportsForSelectedBlueprint,
  isLoadingReportsForSelectedBlueprint,
  selectedBlueprint,
  isStepCompleted,
  isSavingCompletedStep,
  handleLoadReportsForSelectedBlueprint,
  saveCompletedStep,
}: ReviewReportResultsStepProps) => {
  const [selectedReport, setSelectedReport] = useState(null);

  const handleReportChange = (value: string) => {
    const report = reportsForSelectedBlueprint.filter((rep) => rep.id === value)[0];
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setSelectedReport(report);
  };

  const hasReportsCreate = useProductFeature(ProductFeatureName.ReportsCreate);

  return (
    <>
      <Paragraph color='primary'>
        As you saw in the previous step, the Workflow Run created a Report.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>
        A Report adds additional data to the information that an end user provided in an Inquiry.
        Persona offers many types of Reports that are powered by thousands of authoritative data
        sources, including global watchlists, adverse media, and email/phone risk reports.
      </Paragraph>
      <Spacer height={2} />
      <Paragraph color='primary'>Select a Report to view its results:</Paragraph>
      <Spacer height={1} />
      <Select
        aria-label='Select a Report'
        disabled={isLoadingReportsForSelectedBlueprint || reportsForSelectedBlueprint.length === 0}
        placeholder={
          reportsForSelectedBlueprint.length === 0
            ? 'No Reports for this solution yet!'
            : 'Choose a Report...'
        }
        onValueChange={(value) => handleReportChange(value)}
      >
        {reportsForSelectedBlueprint.map((rep) => (
          <Option key={`option-${rep.id}`} value={rep.id}>
            Report created on{' '}
            {DateTime.fromISO(rep.attributes['created-at']).toFormat(DATE_AND_TIME_TZ_FORMAT)} (
            {rep.id})
          </Option>
        ))}
      </Select>
      <Spacer height={1} />
      <ButtonRow>
        {isSavingCompletedStep ? (
          <Button disabled width='button'>
            Saving...
          </Button>
        ) : (
          <Button
            id='home-sandbox-onboarding-checklist__view-report'
            disabled={!selectedReport || isLoadingReportsForSelectedBlueprint}
            width='button'
            onClick={() => {
              if (!isStepCompleted) {
                saveCompletedStep();
              }
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              window.open(DashboardPageLink.Report.replace('[report-token]', selectedReport.id));
            }}
          >
            View Report
          </Button>
        )}
        <Button
          id='home-sandbox-onboarding-checklist__refresh-reports-list'
          disabled={isLoadingReportsForSelectedBlueprint}
          variant='basic'
          width='button'
          onClick={() => {
            const reportTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
              (block) => block.type === BlueprintBlockType.ReportTemplate,
            );
            if (reportTemplateBlocks.length > 0) {
              handleLoadReportsForSelectedBlueprint(reportTemplateBlocks.map((rptp) => rptp.token));
            }
          }}
        >
          Reload Reports
        </Button>
      </ButtonRow>
      <Spacer height={3} />
      {!hasReportsCreate && (
        <>
          <GuideStepWarningBanner>
            <Paragraph color='primary'>
              Note: Your current plan doesn’t support Reports, so you may not see Reports created in
              this step. If you would like to change your plan, visit{' '}
              <Link target='_blank' rel='noreferrer' to='/dashboard/billing'>
                this page
              </Link>
              .
            </Paragraph>
          </GuideStepWarningBanner>
          <Spacer height={2} />
        </>
      )}
      <GuideStepTipBanner>
        <Paragraph color='primary'>
          Tip: See our Help Center to learn about the different types of Reports and how to use
          them:
        </Paragraph>
        <UnorderedList>
          <li>
            <a
              href='https://support.withpersona.com/hc/en-us/articles/360055726494-Key-concepts-Inquiry-Verification-and-other-terms'
              target='_blank'
              rel='noreferrer'
            >
              Key concepts: Inquiry, Verification, and other terms
            </a>
          </li>
          <li>
            <a
              href='https://support.withpersona.com/hc/en-us/articles/1500003167041-Reports-overview-'
              target='_blank'
              rel='noreferrer'
            >
              Reports overview
            </a>
          </li>
        </UnorderedList>
      </GuideStepTipBanner>
    </>
  );
};

const ButtonRow = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[2]};
`;

export default ReviewReportResultsStep;
